import React, { useState, useEffect, useRef, useMemo, useCallback, useId } from "react";
import { X, Search, Check, ChevronDown, ChevronRight } from "lucide-react";
import { List, SubTask, Task } from "../../types";
import { stripHtmlAndImages } from "../../lib/utils";
import { TrappedPanel } from "../../hooks/useDialogFocus";
import { FullWindowLayer } from "../ui/FullWindowLayer";

export type TaskPickerSelection =
  | { kind: "task"; task: Task }
  | { kind: "subtask"; task: Task; subtask: SubTask };

type SelectionId =
  | { kind: "task"; taskId: string }
  | { kind: "subtask"; taskId: string; subtaskId: string };

interface TaskPickerDialogProps {
  tasks: Task[];
  lists: List[];
  onSelect: (selection: TaskPickerSelection) => void;
  onClose: () => void;
  excludeTaskIds?: string[];
  allowSubtaskTarget?: boolean;
  placeholder?: string;
  description?: string;
}

interface VisibleTask {
  task: Task;
  subtasks: SubTask[];
}

interface Group {
  listKey: string;
  list: List | null;
  tasks: VisibleTask[];
}

const NO_LIST_KEY = "__no_list__";

const selectionIdKey = (s: SelectionId): string =>
  s.kind === "task" ? `task:${s.taskId}` : `subtask:${s.taskId}:${s.subtaskId}`;

const sameSelection = (a: SelectionId | null, b: SelectionId): boolean => {
  if (!a) return false;
  return selectionIdKey(a) === selectionIdKey(b);
};

const TaskPickerDialog: React.FC<TaskPickerDialogProps> = ({
  tasks,
  lists,
  onSelect,
  onClose,
  excludeTaskIds,
  allowSubtaskTarget = false,
  placeholder = "Search tasks...",
  description,
}) => {
  const radioName = useId();
  const [inputValue, setInputValue] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [collapsedListKeys, setCollapsedListKeys] = useState<Set<string>>(
    new Set(),
  );
  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<SelectionId | null>(null);
  const isConfirmingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(inputValue), 200);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Cache the lowercased title and HTML-stripped description per task
  // so retyping the search doesn't re-parse every description on every
  // keystroke.
  const searchableTexts = useMemo(() => {
    const cache = new Map<string, { title: string; description: string }>();
    for (const task of tasks) {
      cache.set(task.id, {
        title: task.title.toLowerCase(),
        description: stripHtmlAndImages(task.description ?? "").toLowerCase(),
      });
    }
    return cache;
  }, [tasks]);

  // Re-sort by order_position even if the parent passed them out of
  // order, so the group order in the picker is always deterministic.
  const listsSorted = useMemo(
    () => [...lists].sort((a, b) => a.order_position - b.order_position),
    [lists],
  );

  // Stable key for the excludeTaskIds prop: callers pass an inline
  // array (`{[someId]}`), so referential equality would bust the
  // groupedView memo on every parent render.
  const excludeKey = useMemo(
    () => (excludeTaskIds ?? []).join("|"),
    [excludeTaskIds],
  );

  const groupedView = useMemo<Group[]>(() => {
    const term = debouncedTerm.trim().toLowerCase();
    const excludeSet = new Set(excludeKey ? excludeKey.split("|") : []);
    const knownListIds = new Set(listsSorted.map((l) => l.id));

    const visibleTasks: VisibleTask[] = [];
    for (const task of tasks) {
      if (excludeSet.has(task.id)) continue;
      if (!showCompleted && task.completed) continue;

      const allSubtasks = task.subtasks ?? [];
      const subtasksByCompletion = showCompleted
        ? allSubtasks
        : allSubtasks.filter((st) => !st.completed);

      if (term) {
        const searchable = searchableTexts.get(task.id);
        if (!searchable) continue;
        const titleMatch = searchable.title.includes(term);
        const descMatch = searchable.description.includes(term);
        const subtaskMatches = subtasksByCompletion.filter((st) =>
          st.title.toLowerCase().includes(term),
        );
        if (!titleMatch && !descMatch && subtaskMatches.length === 0) continue;
        visibleTasks.push({
          task,
          // Title hit: the user is navigating to this task, show all
          // its sub-tasks so they can pick from them. Description-only
          // or sub-task-only hit: only show the matching sub-tasks so
          // the result stays focused on what the user typed.
          subtasks: titleMatch ? subtasksByCompletion : subtaskMatches,
        });
      } else {
        visibleTasks.push({ task, subtasks: subtasksByCompletion });
      }
    }

    // Sort by order_position asc, updated_at desc as the tiebreaker.
    // Many older / imported tasks share order_position=0; without the
    // tiebreaker the apparent order would be whatever the data layer
    // happened to return.
    const sortByOrder = (a: VisibleTask, b: VisibleTask) => {
      const orderDiff = a.task.order_position - b.task.order_position;
      if (orderDiff !== 0) return orderDiff;
      return (
        new Date(b.task.updated_at).getTime() -
        new Date(a.task.updated_at).getTime()
      );
    };

    const groups: Group[] = [];
    for (const list of listsSorted) {
      const groupTasks = visibleTasks
        .filter((v) => v.task.list_id === list.id)
        .sort(sortByOrder);
      if (groupTasks.length > 0) {
        groups.push({ listKey: list.id, list, tasks: groupTasks });
      }
    }
    // "No list" also catches orphan tasks whose list_id points at a
    // list that isn't in the lists prop (deleted list, parent passed a
    // filtered subset). Without this fallback the task would silently
    // disappear from the picker.
    const listless = visibleTasks
      .filter(
        (v) => v.task.list_id === null || !knownListIds.has(v.task.list_id),
      )
      .sort(sortByOrder);
    if (listless.length > 0) {
      groups.push({ listKey: NO_LIST_KEY, list: null, tasks: listless });
    }
    return groups;
  }, [
    tasks,
    listsSorted,
    debouncedTerm,
    showCompleted,
    excludeKey,
    searchableTexts,
  ]);

  const searchActive = debouncedTerm.trim().length > 0;

  const isListExpanded = useCallback(
    (key: string) => searchActive || !collapsedListKeys.has(key),
    [searchActive, collapsedListKeys],
  );
  const isTaskExpanded = useCallback(
    (taskId: string) => searchActive || expandedTaskIds.has(taskId),
    [searchActive, expandedTaskIds],
  );

  const toggleList = (key: string) => {
    setCollapsedListKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleTask = (id: string) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Walk the rendered tree to compute what's actually on screen right
  // now — both to gate the Confirm button against hidden selections
  // and to drop a stale selection when its row is no longer visible
  // (collapsed parent, collapsed list, refined search, completed
  // toggle flipped).
  const visibleSelections = useMemo(() => {
    const visible = new Set<string>();
    for (const group of groupedView) {
      if (!isListExpanded(group.listKey)) continue;
      for (const v of group.tasks) {
        visible.add(selectionIdKey({ kind: "task", taskId: v.task.id }));
        if (allowSubtaskTarget && isTaskExpanded(v.task.id)) {
          for (const st of v.subtasks) {
            visible.add(
              selectionIdKey({
                kind: "subtask",
                taskId: v.task.id,
                subtaskId: st.id,
              }),
            );
          }
        }
      }
    }
    return visible;
  }, [groupedView, isListExpanded, isTaskExpanded, allowSubtaskTarget]);

  useEffect(() => {
    if (selected && !visibleSelections.has(selectionIdKey(selected))) {
      setSelected(null);
    }
  }, [visibleSelections, selected]);

  const resolveSelection = useCallback(
    (s: SelectionId | null): TaskPickerSelection | null => {
      if (!s) return null;
      const task = tasks.find((t) => t.id === s.taskId);
      if (!task) return null;
      if (s.kind === "task") return { kind: "task", task };
      const subtask = task.subtasks?.find((st) => st.id === s.subtaskId);
      if (!subtask) return null;
      return { kind: "subtask", task, subtask };
    },
    [tasks],
  );

  const canConfirm =
    !!selected && visibleSelections.has(selectionIdKey(selected));

  const confirm = useCallback(() => {
    if (isConfirmingRef.current) return;
    if (!selected) return;
    if (!visibleSelections.has(selectionIdKey(selected))) return;
    const sel = resolveSelection(selected);
    if (!sel) return;
    isConfirmingRef.current = true;
    onSelect(sel);
  }, [selected, visibleSelections, resolveSelection, onSelect]);

  // Capture-phase Escape so stacked dialogs (picker over TaskDialog)
  // only dismiss the topmost one. Enter follows the same
  // explicit-selection rule the Confirm button does — it never fires
  // on an empty selection.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Enter") {
        if (!canConfirm) return;
        e.preventDefault();
        confirm();
      }
    };
    document.addEventListener("keydown", handleKey, true);
    return () => document.removeEventListener("keydown", handleKey, true);
  }, [onClose, canConfirm, confirm]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const isEmpty = groupedView.length === 0;

  return (
    <FullWindowLayer
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-2 sm:p-4 animate-dialog-backdrop"
      onBackdropClick={onClose}
    >
      <TrappedPanel
        aria-label="Pick a task"
        className="bg-warm-bg-card rounded-xl border border-warm-border w-full max-w-lg max-h-[90vh] sm:max-h-[70vh] flex flex-col animate-dialog-panel"
      >
        {/* Search input */}
        <div className="p-3 sm:p-4 border-b border-warm-border">
          <div className="flex items-center gap-2 sm:gap-3">
            <Search
              className="text-warm-text-tertiary flex-shrink-0"
              size={20}
            />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholder}
              className="flex-1 min-w-0 bg-transparent text-base sm:text-lg text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none"
            />
            {inputValue && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setInputValue("");
                  inputRef.current?.focus();
                }}
                className="text-warm-text-tertiary hover:text-warm-text-secondary transition-colors"
              >
                <X size={20} />
              </button>
            )}
            <button
              type="button"
              aria-label="Close picker"
              onClick={onClose}
              className="text-warm-text-tertiary hover:text-warm-text-secondary transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          {description && (
            <p className="mt-2 ml-7 sm:ml-8 text-xs text-warm-text-tertiary leading-snug">
              {description}
            </p>
          )}
        </div>

        {/* Results */}
        <div
          role="radiogroup"
          aria-label="Select a task"
          className="flex-1 overflow-y-auto py-1"
        >
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center py-12 text-warm-text-tertiary">
              <Search size={40} className="mb-3 opacity-40" />
              <p className="text-sm">
                {debouncedTerm.trim()
                  ? `No tasks match '${debouncedTerm}'`
                  : "No tasks yet"}
              </p>
            </div>
          ) : (
            groupedView.map((group) => {
              const expanded = isListExpanded(group.listKey);
              return (
                <div key={group.listKey} className="pb-1">
                  <button
                    type="button"
                    onClick={() => toggleList(group.listKey)}
                    aria-expanded={expanded}
                    aria-label={
                      expanded
                        ? `Collapse ${group.list?.name ?? "No list"} group`
                        : `Expand ${group.list?.name ?? "No list"} group`
                    }
                    disabled={searchActive}
                    className="w-full flex items-center gap-1.5 px-3 pt-3 pb-1.5 text-[11px] uppercase tracking-wide text-warm-text-tertiary font-semibold hover:text-warm-text-secondary transition-colors disabled:cursor-default disabled:hover:text-warm-text-tertiary"
                  >
                    <span className="inline-flex items-center justify-center w-4 h-4 flex-shrink-0">
                      {searchActive ? null : expanded ? (
                        <ChevronDown size={12} />
                      ) : (
                        <ChevronRight size={12} />
                      )}
                    </span>
                    {group.list ? (
                      <>
                        <span className="text-sm leading-none">
                          {group.list.emoji}
                        </span>
                        <span className="truncate">{group.list.name}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm leading-none">📂</span>
                        <span className="truncate">No list</span>
                      </>
                    )}
                  </button>
                  {expanded &&
                    group.tasks.map(({ task, subtasks }) => {
                      const hasVisibleSubtasks = subtasks.length > 0;
                      const subtasksVisible = isTaskExpanded(task.id);
                      const taskSel: SelectionId = {
                        kind: "task",
                        taskId: task.id,
                      };
                      const isSelected = sameSelection(selected, taskSel);
                      return (
                        <React.Fragment key={task.id}>
                          <div className="w-full flex items-center gap-1 pl-7 pr-4">
                            {hasVisibleSubtasks && !searchActive ? (
                              <button
                                type="button"
                                onClick={() => toggleTask(task.id)}
                                aria-expanded={subtasksVisible}
                                aria-label={
                                  subtasksVisible
                                    ? `Collapse sub-tasks of ${task.title}`
                                    : `Expand sub-tasks of ${task.title}`
                                }
                                className="w-5 h-5 inline-flex items-center justify-center text-warm-text-tertiary hover:text-warm-text-secondary transition-colors flex-shrink-0"
                              >
                                {subtasksVisible ? (
                                  <ChevronDown size={14} />
                                ) : (
                                  <ChevronRight size={14} />
                                )}
                              </button>
                            ) : (
                              <span
                                aria-hidden="true"
                                className="w-5 h-5 flex-shrink-0"
                              />
                            )}
                            <label className="flex-1 min-w-0 flex items-center gap-2.5 py-2 px-2 cursor-pointer hover:bg-warm-bg-elevated rounded-soft transition-colors">
                              <input
                                type="radio"
                                name={radioName}
                                checked={isSelected}
                                onChange={() => setSelected(taskSel)}
                                className="peer sr-only"
                              />
                              <span
                                aria-hidden="true"
                                className={`inline-flex items-center justify-center w-4 h-4 rounded-full border-2 transition-colors duration-150 flex-shrink-0 peer-focus-visible:ring-2 peer-focus-visible:ring-warm-blue peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-warm-bg-card ${
                                  isSelected
                                    ? "border-warm-blue"
                                    : "border-warm-border"
                                }`}
                              >
                                {isSelected && (
                                  <span className="w-2 h-2 rounded-full bg-warm-blue" />
                                )}
                              </span>
                              <span
                                className={`flex-1 truncate text-sm font-medium ${
                                  task.completed
                                    ? "text-warm-text-tertiary line-through"
                                    : "text-warm-text-primary"
                                }`}
                              >
                                {task.title}
                              </span>
                              {hasVisibleSubtasks && (
                                <span className="text-[10px] text-warm-text-tertiary tabular-nums">
                                  {subtasks.length}
                                </span>
                              )}
                              {task.completed && (
                                <span className="text-[10px] text-warm-text-tertiary">
                                  Completed
                                </span>
                              )}
                            </label>
                          </div>
                          {subtasksVisible &&
                            subtasks.map((subtask) => {
                              const subSel: SelectionId = {
                                kind: "subtask",
                                taskId: task.id,
                                subtaskId: subtask.id,
                              };
                              const isSubSelected = sameSelection(
                                selected,
                                subSel,
                              );
                              if (!allowSubtaskTarget) {
                                return (
                                  <div
                                    key={subtask.id}
                                    className="w-full flex items-center gap-2.5 pl-20 pr-4 py-1.5 text-sm opacity-70"
                                  >
                                    <span
                                      className={`flex-1 truncate ${
                                        subtask.completed
                                          ? "text-warm-text-tertiary line-through"
                                          : "text-warm-text-secondary"
                                      }`}
                                    >
                                      {subtask.title}
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <label
                                  key={subtask.id}
                                  className="w-full flex items-center gap-2.5 pl-20 pr-4 py-1.5 text-sm cursor-pointer hover:bg-warm-bg-elevated transition-colors"
                                >
                                  <input
                                    type="radio"
                                    name={radioName}
                                    checked={isSubSelected}
                                    onChange={() => setSelected(subSel)}
                                    className="peer sr-only"
                                  />
                                  <span
                                    aria-hidden="true"
                                    className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border-2 transition-colors duration-150 flex-shrink-0 peer-focus-visible:ring-2 peer-focus-visible:ring-warm-blue peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-warm-bg-card ${
                                      isSubSelected
                                        ? "border-warm-blue"
                                        : "border-warm-border"
                                    }`}
                                  >
                                    {isSubSelected && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-warm-blue" />
                                    )}
                                  </span>
                                  <span
                                    className={`flex-1 truncate ${
                                      subtask.completed
                                        ? "text-warm-text-tertiary line-through"
                                        : "text-warm-text-secondary"
                                    }`}
                                  >
                                    {subtask.title}
                                  </span>
                                </label>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-warm-border flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-warm-text-secondary hover:text-warm-text-primary transition-colors">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              className={`inline-flex items-center justify-center w-4 h-4 rounded border transition-colors duration-150 flex-shrink-0 peer-focus-visible:ring-2 peer-focus-visible:ring-warm-blue peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-warm-bg-card ${
                showCompleted
                  ? "bg-warm-blue border-warm-blue text-white"
                  : "bg-warm-bg-card border-warm-border"
              }`}
            >
              {showCompleted && <Check size={12} strokeWidth={3} />}
            </span>
            Show completed tasks
          </label>
          <div className="flex-1" />
          <button
            type="button"
            disabled={!canConfirm}
            onClick={confirm}
            className="px-4 py-1.5 text-sm font-medium rounded-soft bg-warm-blue text-white hover:bg-warm-blue-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
        </div>
      </TrappedPanel>
    </FullWindowLayer>
  );
};

export default TaskPickerDialog;
